<script setup>
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'

const asset = (path) => `${import.meta.env.BASE_URL}${path}`
const swiperInstance = ref(null)
const canSlidePrev = ref(false)
const canSlideNext = ref(true)

const topGames = [
  { image: 'top/top-decor-1.png', bg: 'top/top-bg-1.png', name: 'Sweet Bonanza 1000' },
  { image: 'top/top-decor-2.png', bg: 'top/top-bg-2.png', name: 'Gates of Olympus' },
  { image: 'top/top-decor-3.png', bg: 'top/top-bg-3.png', name: 'Book of Dead' },
  { image: 'top/top-decor-4.png', bg: 'top/top-bg-4.png', name: 'Big Bass Bonanza' },
  { image: 'top/top-decor-5.png', bg: 'top/top-bg-5.png', name: 'Sugar Rush' },
  { image: 'top/top-decor-6.png', bg: 'top/top-bg-6.png', name: 'Starburst' },
  { image: 'top/top-decor-7.png', bg: 'top/top-bg-7.png', name: 'Legacy of Dead' },
]

function setSwiper(swiper) {
  swiperInstance.value = swiper
  updateNavigation(swiper)
}

function slidePrev() {
  swiperInstance.value?.slidePrev()
}

function slideNext() {
  swiperInstance.value?.slideNext()
}

function updateNavigation(swiper = swiperInstance.value) {
  if (!swiper) return

  canSlidePrev.value = !swiper.isBeginning
  canSlideNext.value = !swiper.isEnd
}

function rankClass(index) {
  return ['top-games__rank--gold', 'top-games__rank--silver', 'top-games__rank--bronze'][index]
}
</script>

<template>
  <section class="top-games" aria-labelledby="top-games-title">
    <div class="top-games__inner">
      <div class="top-games__header">
        <h2 id="top-games-title" class="top-games__title">
          <img :src="asset('top/top-ico.svg')" alt="" aria-hidden="true" />
          <span>Top</span>
        </h2>

        <div class="top-games__actions">
          <button
            class="top-games__arrow"
            type="button"
            aria-label="Previous games"
            :disabled="!canSlidePrev"
            @click="slidePrev"
          >
            <span class="top-games__arrow-icon top-games__arrow-icon--prev" aria-hidden="true"></span>
          </button>
          <button
            class="top-games__arrow"
            type="button"
            aria-label="Next games"
            :disabled="!canSlideNext"
            @click="slideNext"
          >
            <span class="top-games__arrow-icon top-games__arrow-icon--next" aria-hidden="true"></span>
          </button>
          <button class="top-games__see-all" type="button">See All</button>
        </div>
      </div>

      <div
        class="top-games__slider-frame app-swiper-fade"
        :class="{
          'app-swiper-fade--left': canSlidePrev,
          'app-swiper-fade--right': canSlideNext,
        }"
      >
        <Swiper
          class="top-games__slider"
          :slides-per-view="2.15"
          :space-between="20"
          :breakpoints="{
            520: { slidesPerView: 3.15, spaceBetween: 22 },
            768: { slidesPerView: 4.2, spaceBetween: 28 },
            1024: { slidesPerView: 5.2, spaceBetween: 34 },
            1280: { slidesPerView: 6.2, spaceBetween: 40 },
          }"
          @swiper="setSwiper"
          @slideChange="updateNavigation"
          @resize="updateNavigation"
        >
          <SwiperSlide v-for="(game, index) in topGames" :key="game.name">
            <article class="top-games__card" :style="{ '--card-bg': `url(${asset(game.bg)})` }">
              <img class="top-games__image" :src="asset(game.image)" :alt="game.name" />
              <span class="top-games__rank" :class="rankClass(index)">{{ index + 1 }}</span>
            </article>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<style scoped>
.top-games {
  width: 100%;
  
}

.top-games__inner {
  width: min(100%, 1280px);
  margin: 0 auto;
  padding: 18px 16px 40px;
  overflow: hidden;
  
}

.top-games__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.top-games__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.top-games__title img {
  width: 28px;
  height: 28px;
}

.top-games__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.top-games__arrow,
.top-games__see-all {
  border: 0;
  border-radius: 5px;
  color: #fff;
  background: var(--color-accent);
}

.top-games__arrow {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid transparent;
  background:
    var(--gradient-background) padding-box,
    var(--gradient-border) border-box;
  line-height: 0;
  transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  vertical-align: middle;
}

.top-games__arrow:disabled {
  cursor: default;
  opacity: 0.55;
}

.top-games__arrow:not(:disabled):hover {
  background:
    var(--gradient-background-hover) padding-box,
    var(--gradient-border-hover) border-box;
  box-shadow: var(--shadow-control-hover);
}

.top-games__arrow-icon {
  display: block;
  width: 13px;
  height: 13px;
  border-top: 5px solid currentColor;
  border-right: 5px solid currentColor;
}

.top-games__arrow-icon--prev {
  transform: translateX(2px) rotate(-135deg);
}

.top-games__arrow-icon--next {
  transform: translateX(-2px) rotate(45deg);
}

.top-games__see-all {
  min-width: 96px;
  height: 42px;
  padding: 0 18px;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.top-games__see-all:hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-accent-hover);
}

.top-games__slider {
  overflow: visible;
}

.top-games__slider-frame {
  --app-swiper-fade-offset: 16px;
}

.top-games__card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 111.075 / 155.504;

  border: 1px solid var(--color-gold);
  border-radius: 7px;

  background:
    var(--card-bg) center / cover no-repeat,
    #05358f; /* fallback */
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.top-games__card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
  border-color: rgba(255, 212, 52, 0.95);
}
.top-games__card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;

  background: linear-gradient(
    to bottom,
    rgba(0,0,0,0.2),
    rgba(0,0,0,0.6)
  );

  z-index: 0;
}

.top-games__image {
  position: relative;
  z-index: 1;
  left: 50%;
  width: 110%;
  max-width: none;
  height: auto;
  object-fit: contain;
  transform: translateX(-50%) scale(1.2);
  transform-origin: center;
  animation: top-games-decor-pulse 2.4s ease-in-out infinite;
}

.top-games__rank {
  position: absolute;
  right: 50%;
  bottom: -16px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--color-gold);
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(180deg, #2b246d 0%, #81612a 100%);
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1;
  transform: translateX(50%);
}

.top-games__rank--gold {
  border-color: #ffe47a;
  color: #2a1700;
  background: linear-gradient(180deg, #fff4a8 0%, #ffd334 46%, #b87805 100%);
}

.top-games__rank--silver {
  border-color: #f5f7fb;
  color: #171b24;
  background: linear-gradient(180deg, #ffffff 0%, #cfd6df 48%, #7f8793 100%);
}

.top-games__rank--bronze {
  border-color: #f2b277;
  color: #2b1300;
  background: linear-gradient(180deg, #ffd0a1 0%, #c9792d 50%, #7a3d16 100%);
}

@keyframes top-games-decor-pulse {
  0%,
  100% {
    transform: translateX(-50%) scale(1.2);
  }

  50% {
    transform: translateX(-50%) scale(1.3);
  }
}

@media (max-width: 520px) {
  .top-games__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .top-games__actions {
    width: 100%;
  }

  .top-games__see-all {
    margin-left: auto;
  }

}

@media (min-width: 768px) {
  .top-games__inner {
    padding: 24px 48px 56px;
  }

  .top-games__header {
    margin-bottom: 28px;
  }

  .top-games__title {
    font-size: 1.5rem;
  }

  .top-games__title img {
    width: 32px;
    height: 32px;
  }

  .top-games__arrow {
    width: 44px;
    height: 44px;
  }

  .top-games__see-all {
    min-width: 110px;
    height: 44px;
  }

  .top-games__slider-frame {
    --app-swiper-fade-offset: 48px;
  }
}
</style>
